import { Check, Lock, Play } from 'lucide-react';
import { href } from '../app/router';
import { useAppState } from '../state/store';
import { episodes, episodeStatus, lockOf, lockText, minutes, SEASONS, type EpisodeInfo } from '../stories';
import type { Persisted } from '../state/model';

/** The story episodes, season by season. */
export function Stories() {
  const state = useAppState();
  const seasons = [...new Set(episodes.map((e) => e.season))];
  // the next one to open shows how many lessons to go
  const firstLocked = episodes.find((e) => lockOf(state, e))?.id;
  return (
    <main className="page stories" id="main">
      <header className="stack" style={{ gap: 6 }}>
        <p className="eyebrow">Stories</p>
        <h1 className="title">Greek myths in easy Greek</h1>
        <p className="subtitle">
          Short cartoons you can follow from your first week. Each one shows you its words first, then tells the story with them. A new one opens every two lessons.
        </p>
      </header>

      {seasons.map((season) => (
        <section key={season} className="stack" aria-labelledby={`season-${season}`}>
          <div className="stack" style={{ gap: 2 }}>
            <p className="eyebrow">Season {season}</p>
            <h2 className="title-md" id={`season-${season}`}>
              <span lang="el">{SEASONS[season]?.title}</span>
              <span className="muted"> · {SEASONS[season]?.titleEn}</span>
            </h2>
          </div>
          <ol className="episodes">
            {episodes
              .filter((e) => e.season === season)
              .map((ep) => (
                <EpisodeCard key={ep.id} ep={ep} state={state} firstLocked={ep.id === firstLocked} />
              ))}
          </ol>
        </section>
      ))}

      <p className="small muted">More episodes are on the way: the story of Zeus goes on.</p>
    </main>
  );
}

function EpisodeCard({ ep, state, firstLocked }: { ep: EpisodeInfo; state: Persisted; firstLocked: boolean }) {
  const status = episodeStatus(state, ep);
  const kept = state.profile.stories?.[ep.id]?.kept?.length ?? 0;
  const lock = lockOf(state, ep);
  if (lock) {
    // Not a link: it can't be watched yet. Its poster and title still show what is coming.
    return (
      <li className="episode" data-status="locked">
        <div className="episode-link">
          <span className="episode-poster">
            <img src={ep.poster} alt="" width={960} height={540} loading="lazy" decoding="async" />
            <span className="episode-play" aria-hidden="true">
              <Lock size={18} strokeWidth={2.5} />
            </span>
          </span>
          <span className="episode-text">
            <span className="episode-meta">
              Episode {ep.number} · {minutes(ep.duration)}
            </span>
            <span className="episode-title" lang="el">
              {ep.title}
            </span>
            <span className="episode-en">{ep.titleEn}</span>
            <span className="episode-status">{lockText(lock, firstLocked)}</span>
          </span>
        </div>
      </li>
    );
  }
  return (
    <li className="episode" data-status={status}>
      <a className="episode-link" href={href(`/story/${ep.id}`)}>
        <span className="episode-poster">
          <img src={ep.poster} alt="" width={960} height={540} loading="lazy" decoding="async" />
          <span className="episode-play" aria-hidden="true">
            {status === 'watched' ? <Check size={20} strokeWidth={3} /> : <Play size={20} fill="currentColor" strokeWidth={1.5} style={{ marginLeft: 2 }} />}
          </span>
        </span>
        <span className="episode-text">
          <span className="episode-meta">
            Episode {ep.number} · {minutes(ep.duration)}
          </span>
          <span className="episode-title" lang="el">
            {ep.title}
          </span>
          <span className="episode-en">{ep.titleEn}</span>
          <span className="episode-status">
            {status === 'watched'
              ? kept > 0
                ? `Watched · ${kept} ${kept === 1 ? 'phrase' : 'phrases'} kept`
                : 'Watched'
              : status === 'next'
                ? ep.number === 1
                  ? 'Start here'
                  : 'Up next'
                : `Best after episode ${ep.number - 1}`}
          </span>
        </span>
      </a>
    </li>
  );
}
